// Molecule Studio AI - Core Type Definitions

// Element properties for standard chemical elements
export interface ElementProperties {
  symbol: string;
  name: string;
  atomicNumber: number;
  atomicMass: number;
  vanDerWaalsRadius: number; // in Angstroms
  covalentRadius: number; // in Angstroms
  color: string; // CPK coloring
  defaultCPKColor: string;
}

// Standard element definitions (CPK coloring)
export const ELEMENTS: Record<string, ElementProperties> = {
  H: {
    symbol: 'H',
    name: 'Hydrogen',
    atomicNumber: 1,
    atomicMass: 1.008,
    vanDerWaalsRadius: 1.2,
    covalentRadius: 0.31,
    color: '#FFFFFF',
    defaultCPKColor: '#FFFFFF',
  },
  C: {
    symbol: 'C',
    name: 'Carbon',
    atomicNumber: 6,
    atomicMass: 12.011,
    vanDerWaalsRadius: 1.7,
    covalentRadius: 0.76,
    color: '#404040',
    defaultCPKColor: '#909090',
  },
  N: {
    symbol: 'N',
    name: 'Nitrogen',
    atomicNumber: 7,
    atomicMass: 14.007,
    vanDerWaalsRadius: 1.55,
    covalentRadius: 0.71,
    color: '#3050F8',
    defaultCPKColor: '#3050F8',
  },
  O: {
    symbol: 'O',
    name: 'Oxygen',
    atomicNumber: 8,
    atomicMass: 15.999,
    vanDerWaalsRadius: 1.52,
    covalentRadius: 0.66,
    color: '#FF0D0D',
    defaultCPKColor: '#FF0D0D',
  },
  F: {
    symbol: 'F',
    name: 'Fluorine',
    atomicNumber: 9,
    atomicMass: 18.998,
    vanDerWaalsRadius: 1.47,
    covalentRadius: 0.57,
    color: '#90E050',
    defaultCPKColor: '#90E050',
  },
  P: {
    symbol: 'P',
    name: 'Phosphorus',
    atomicNumber: 15,
    atomicMass: 30.974,
    vanDerWaalsRadius: 1.8,
    covalentRadius: 1.07,
    color: '#FF8000',
    defaultCPKColor: '#FF8000',
  },
  S: {
    symbol: 'S',
    name: 'Sulfur',
    atomicNumber: 16,
    atomicMass: 32.065,
    vanDerWaalsRadius: 1.8,
    covalentRadius: 1.05,
    color: '#FFFF30',
    defaultCPKColor: '#FFFF30',
  },
  Cl: {
    symbol: 'Cl',
    name: 'Chlorine',
    atomicNumber: 17,
    atomicMass: 35.45,
    vanDerWaalsRadius: 1.75,
    covalentRadius: 1.02,
    color: '#1FF01F',
    defaultCPKColor: '#1FF01F',
  },
  Br: {
    symbol: 'Br',
    name: 'Bromine',
    atomicNumber: 35,
    atomicMass: 79.904,
    vanDerWaalsRadius: 1.85,
    covalentRadius: 1.14,
    color: '#A62929',
    defaultCPKColor: '#A62929',
  },
  I: {
    symbol: 'I',
    name: 'Iodine',
    atomicNumber: 53,
    atomicMass: 126.904,
    vanDerWaalsRadius: 1.98,
    covalentRadius: 1.33,
    color: '#940094',
    defaultCPKColor: '#940094',
  },
};

// Bond type definitions
export enum BondType {
  SINGLE = 1,
  DOUBLE = 2,
  TRIPLE = 3,
  AROMATIC = 4,
}

export interface Bond {
  id: string;
  atomIndex1: number; // Index in the atoms array
  atomIndex2: number;
  type: BondType;
  order: number; // 1, 2, 3, or 1.5 for aromatic
}

// Atom in 3D space
export interface Atom {
  id: string;
  element: string;
  x: number; // Position in Angstroms
  y: number;
  z: number;
  formalCharge: number;
  implicitHydrogens: number;
  isotope: number;
  aromatic: boolean;
  chiral: boolean;
  chiralTag?: number;
}

// Complete molecule structure
export interface Molecule {
  id: string;
  name: string;
  formula: string;
  atoms: Atom[];
  bonds: Bond[];
  molecularWeight: number;
  canonicalSMILES: string;
  inchiKey?: string;
  inchI?: string;
  charge: number;
  spinMultiplicity: number;
  createdAt: number;
  source?: 'smiles' | 'inchi' | 'pdb' | 'mol' | 'example' | 'api';
}

// Visualization styles
export enum RenderMode {
  BALL_AND_STICK = 'ball-and-stick',
  SPACE_FILLING = 'space-filling',
  WIREFRAME = 'wireframe',
  STICK = 'stick',
  RIBBON = 'ribbon',
}

export interface VisualizationSettings {
  renderMode: RenderMode;
  atomScale: number;
  bondThickness: number;
  showHydrogens: boolean;
  showAtomLabels: boolean;
  showBonds: boolean;
  backgroundColor: string;
  ambientLightIntensity: number;
  directionalLightIntensity: number;
  enableShadows: boolean;
  antialiasing: boolean;
  enableSSAO: boolean;
  colorScheme: 'cpk' | 'atomic' | 'custom';
  atomColors: Record<string, string>;
}

// Camera state for persistence
export interface CameraState {
  position: [number, number, number];
  target: [number, number, number];
  zoom: number;
  fov: number;
}

// Camera controls settings
export interface CameraControls {
  enableRotate: boolean;
  enableZoom: boolean;
  enablePan: boolean;
  rotateSpeed: number;
  zoomSpeed: number;
  panSpeed: number;
  minDistance: number;
  maxDistance: number;
  minPolarAngle: number;
  maxPolarAngle: number;
}

// Molecular properties for drug-likeness analysis
export interface MolecularProperties {
  molecularWeight: number;
  exactMass: number;
  formula: string;
  atomCount: number;
  bondCount: number;
  ringCount: number;
  rotatableBondCount: number;
  hydrogenBondDonorCount: number;
  hydrogenBondAcceptorCount: number;
  topologicalPolarSurfaceArea: number;
  logP: number;
  formalCharge: number;
  heavyAtomCount: number;
  carbonAtomCount: number;
  nitrogenAtomCount: number;
  oxygenAtomCount: number;
  sulfurAtomCount: number;
  halogenCount: number;
  aromaticRingCount: number;
  saturatedRingCount: number;
  heteroatomCount: number;
  ruleOfFiveViolations: number;
  drugLikenessScore: number;
  drugLikenessClass: 'drug-like' | 'borderline' | 'non-drug-like';
  atomComposition: Record<string, number>;
}

// Lipinski's Rule of Five criteria
export interface LipinskiData {
  molWt: number;
  logP: number;
  hbd: number;
  hba: number;
  violations: number;
  pass: boolean;
}

// PDB file format atom
export interface PDBAtom {
  serial: number;
  name: string;
  altLoc: string;
  resName: string;
  chainId: string;
  resSeq: number;
  x: number;
  y: number;
  z: number;
  occupancy: number;
  tempFactor: number;
  element: string;
  charge: number;
}

// Parsed PDB structure
export interface PDBStructure {
  header: string;
  title: string;
  resolution?: number;
  experimentalMethod?: string;
  atoms: PDBAtom[];
  bonds: Bond[];
  chains: string[];
  residues: { name: string; chain: string; seqNum: number }[];
}

// Export formats
export type ExportFormat = 'pdb' | 'mol' | 'mol2' | 'sdf' | 'png' | 'svg';

// Export options
export interface ExportOptions {
  format: ExportFormat;
  includeHydrogens: boolean;
  highResolution: boolean;
  transparentBackground: boolean;
  width: number;
  height: number;
  quality?: number;
}

// Example molecules library
export interface ExampleMolecule {
  id: string;
  name: string;
  smiles: string;
  description: string;
  category: 'drug' | 'natural' | 'basic' | 'complex';
  molecularWeight: number;
}

// Search result from API
export interface MoleculeSearchResult {
  cid: number;
  name: string;
  formula: string;
  molecularWeight: number;
  canonicalSMILES: string;
  isomericSMILES: string;
  inchiKey: string;
  xlogP: number;
  charge: number;
  heavyAtomCount: number;
  complexity: number;
  isotopes: number;
  definedAtomStereoCount: number;
  undefinedAtomStereoCount: number;
  definedBondStereoCount: number;
  undefinedBondStereoCount: number;
}

// API response types
export interface PubChemResponse {
  IdentifierList?: {
    CID: number[];
  };
  PropertyList?: string[];
  InformationList?: {
    Information: PubChemMoleculeInfo[];
  };
}

export interface PubChemMoleculeInfo {
  CID: number;
  Title?: string;
  MolecularFormula?: string;
  MolecularWeight?: number;
  CanonicalSMILES?: string;
  IsomericSMILES?: string;
  InChI?: string;
  InChIKey?: string;
  XLogP?: number;
  ExactMass?: number;
  Charge?: number;
  HeavyAtomCount?: number;
  Complexity?: number;
}

// Animation keyframes
export interface AnimationKeyframe {
  time: number;
  position: [number, number, number];
  rotation?: [number, number, number];
  scale?: [number, number, number];
}

// Molecule animation
export interface MoleculeAnimation {
  id: string;
  moleculeId: string;
  keyframes: AnimationKeyframe[];
  duration: number;
  easing: 'linear' | 'ease-in' | 'ease-out' | 'ease-in-out';
  loop: boolean;
}

